import clsx from 'clsx';
import { EQuestionType } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import HubertSegmentedControl from 'components/HubertUI/HubertSegmentedControl';
import MutedText from 'components/common/MutedText';
import DebouncedInput from 'components/common/DebouncedInput';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestionAllowRetake,
  selectQuestionIsReadonly,
  selectQuestionMaxDuration,
  selectQuestionMaxRetakes,
  selectQuestionMaxThinkingSeconds,
  selectQuestionMinDuration,
  selectQuestionRecordingQuality,
  selectQuestionRecordingRequired,
} from 'rtk/questionEditorSelector';
import {
  updateAllowRetake,
  updateMaxDuration,
  updateMaxRetakes,
  updateMaxThinkingSeconds,
  updateMinDuration,
  updateRecordingQuality,
  updateRecordingRequired,
} from 'rtk/Question.Editor.Slice';
import { useQuestionEditorContext } from '../context/create-question.context';

const parseNumberOrUndefined = (val: string | number | null | undefined): number | undefined => {
  if (val === '' || val === undefined || val === null) return undefined;
  const num = typeof val === 'number' ? val : parseInt(val, 10);
  // The save schema rejects negatives, so treat them as blank rather than block the save.
  return isNaN(num) || num < 0 ? undefined : num;
};

/**
 * Recruiter settings panel for audio / video (recording) questions: max/min recording
 * length, whether re-recording is allowed and how often, the thinking time before the
 * first take, and (video only) the capture quality. These
 * map to the AddPipe recorder params on the candidate side; sensible defaults apply when
 * left blank (audio 120s / video 90s, retakes on, 480p).
 */
const CreateRecording = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const type = useAppSelector((state) => state.questionEditor.byId[questionId]?.type);
  const disabled = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const minDuration = useAppSelector((state) => selectQuestionMinDuration(state, questionId));
  const maxDuration = useAppSelector((state) => selectQuestionMaxDuration(state, questionId));
  const allowRetake = useAppSelector((state) => selectQuestionAllowRetake(state, questionId));
  const maxRetakes = useAppSelector((state) => selectQuestionMaxRetakes(state, questionId));
  const maxThinkingSeconds = useAppSelector((state) => selectQuestionMaxThinkingSeconds(state, questionId));
  const required = useAppSelector((state) => selectQuestionRecordingRequired(state, questionId));
  const quality = useAppSelector((state) => selectQuestionRecordingQuality(state, questionId));

  const isVideo = type === EQuestionType.Video;

  return (
    <div className={clsx('flex flex-col justify-start gap-6', 'p-1')}>
      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel htmlFor="recording-min-duration" className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('recordingSettings.minDurationLabel')}
          </HubertLabel>
          <DebouncedInput
            id="recording-min-duration"
            type="number"
            disabled={disabled}
            value={minDuration ?? ''}
            min={0}
            placeholder={t('recordingSettings.minDurationPlaceholder')}
            onChange={(val) => dispatch(updateMinDuration({ questionId, value: parseNumberOrUndefined(val) }))}
          />
        </div>
        <MutedText text={t('recordingSettings.minDurationHint')} />
      </div>

      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel htmlFor="recording-max-duration" className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('recordingSettings.maxDurationLabel')}
          </HubertLabel>
          <DebouncedInput
            id="recording-max-duration"
            type="number"
            disabled={disabled}
            value={maxDuration ?? ''}
            min={1}
            placeholder={t('recordingSettings.maxDurationPlaceholder', { value: isVideo ? 90 : 120 })}
            onChange={(val) => dispatch(updateMaxDuration({ questionId, value: parseNumberOrUndefined(val) }))}
          />
        </div>
        <MutedText text={t('recordingSettings.maxDurationHint')} />
      </div>

      <div className={clsx('hubert-vertical-center flex flex-row justify-between px-1')}>
        <HubertLabel htmlFor="recording-allow-retake" className="whitespace-nowrap">
          {t('recordingSettings.allowRetakeLabel')}
        </HubertLabel>
        <HubertSwitch
          id="recording-allow-retake"
          disabled={disabled}
          checked={allowRetake}
          onCheckedChange={(checked: boolean) => dispatch(updateAllowRetake({ questionId, value: checked }))}
        />
      </div>

      {allowRetake && (
        <div className="flex flex-col gap-1">
          <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
            <HubertLabel
              htmlFor="recording-max-retakes"
              className={clsx('whitespace-nowrap', disabled && 'opacity-50')}
            >
              {t('recordingSettings.maxRetakesLabel')}
            </HubertLabel>
            <DebouncedInput
              id="recording-max-retakes"
              type="number"
              disabled={disabled}
              value={maxRetakes ?? ''}
              min={0}
              placeholder={t('recordingSettings.maxRetakesPlaceholder')}
              onChange={(val) => dispatch(updateMaxRetakes({ questionId, value: parseNumberOrUndefined(val) }))}
            />
          </div>
          <MutedText text={t('recordingSettings.maxRetakesHint')} />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
          <HubertLabel htmlFor="recording-max-thinking" className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
            {t('recordingSettings.maxThinkingLabel')}
          </HubertLabel>
          <DebouncedInput
            id="recording-max-thinking"
            type="number"
            disabled={disabled}
            value={maxThinkingSeconds ?? ''}
            min={0}
            placeholder={t('recordingSettings.maxThinkingPlaceholder')}
            onChange={(val) => dispatch(updateMaxThinkingSeconds({ questionId, value: parseNumberOrUndefined(val) }))}
          />
        </div>
        <MutedText text={t('recordingSettings.maxThinkingHint')} />
      </div>

      <div className="flex flex-col gap-1">
        <div className={clsx('hubert-vertical-center flex flex-row justify-between px-1')}>
          <HubertLabel htmlFor="recording-required" className="whitespace-nowrap">
            {t('recordingSettings.requiredLabel')}
          </HubertLabel>
          <HubertSwitch
            id="recording-required"
            disabled={disabled}
            checked={required}
            onCheckedChange={(checked: boolean) => dispatch(updateRecordingRequired({ questionId, value: checked }))}
          />
        </div>
        <MutedText text={t('recordingSettings.requiredHint')} />
      </div>

      {isVideo && (
        <div className="flex flex-col gap-1">
          <div className={clsx('flex flex-row justify-start gap-4', 'hubert-vertical-center')}>
            <HubertLabel className={clsx('whitespace-nowrap', disabled && 'opacity-50')}>
              {t('recordingSettings.qualityLabel')}
            </HubertLabel>
            <HubertSegmentedControl
              value={quality}
              disabled={disabled}
              onValueChange={(value: string) => {
                if (value === '360p' || value === '480p' || value === '720p') {
                  dispatch(updateRecordingQuality({ questionId, value }));
                }
              }}
            >
              <HubertSegmentedControl.Item value="360p">360p</HubertSegmentedControl.Item>
              <HubertSegmentedControl.Item value="480p">480p</HubertSegmentedControl.Item>
              <HubertSegmentedControl.Item value="720p">720p</HubertSegmentedControl.Item>
            </HubertSegmentedControl>
          </div>
          <MutedText text={t('recordingSettings.qualityHint')} />
        </div>
      )}
    </div>
  );
};

export default CreateRecording;
